import React, { useEffect, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

/**
 * Vendor picker over /api/vendors/?typeahead=true&search=. `value` is a vendor object
 * ({id, name}) or null; `onChange(vendorOrNull)`.
 */
export default function VendorAutocomplete({ value, onChange, label, placeholder, size = 'small', disabled, testId, sx }) {
  const { t } = useTranslation();
  const [options, setOptions] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await axios.get('/api/vendors/', { params: { typeahead: 'true', search: input || undefined } });
        if (!cancelled) {
          const rows = Array.isArray(response.data) ? response.data : response.data?.results || [];
          setOptions(rows);
        }
      } catch (error) {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [input]);

  return (
    <Autocomplete
      size={size}
      disabled={disabled}
      value={value || null}
      options={options}
      loading={loading}
      getOptionLabel={(option) => option?.name || ''}
      isOptionEqualToValue={(option, selected) => option?.id === selected?.id}
      onChange={(event, newValue) => onChange(newValue)}
      onInputChange={(event, newInput) => setInput(newInput)}
      noOptionsText={t('purchaseLedger.vendor.noMatches')}
      sx={sx}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          inputProps={{ ...params.inputProps, 'data-testid': testId }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
